<script lang="ts" setup>
import { computed, defineProps } from "vue";
import type { CSSProperties } from "vue";

/**
 * Размеры не имеют значений по умолчанию в пропсах намеренно: непереданный
 * размер должен остаться за CSS, иначе inline-стиль перебил бы адаптивные
 * правила вызывающего компонента. Дефолты живут в .base-skeleton ниже.
 */
const props = defineProps({
  width: {
    type: String,
    default: undefined,
  },
  height: {
    type: String,
    default: undefined,
  },
  radius: {
    type: String,
    default: undefined,
  },
});

const styles = computed<CSSProperties>(() => ({
  ...(props.width ? { width: props.width } : {}),
  ...(props.height ? { height: props.height } : {}),
  ...(props.radius ? { borderRadius: props.radius } : {}),
}));
</script>

<template>
  <div class="base-skeleton" :style="styles" aria-hidden="true" />
</template>

<style lang="scss" scoped>
@import "@/assets/style/colors";

/**
 * Заглушка на время загрузки: блок нужного размера, по которому слева направо
 * проходит светлый градиентный блик. Размеры задаёт вызывающий компонент,
 * цвета берутся из токенов темы — блок одинаково выглядит во всех брендах.
 */
.base-skeleton {
  position: relative;
  overflow: hidden;
  // Дефолтные размеры — здесь, а не в пропсах: так их может переопределить
  // и проп (inline), и класс от вызывающего компонента.
  width: 100%;
  height: 16px;
  border-radius: 8px;
  // Не --color-bg-primary: это ровно цвет фона страницы, и заглушки в местах
  // без белой карточки (тариф, опции) на нём не видны. Слегка подкрашенный
  // серый читается и на белом, и на фоне страницы.
  background-color: color-mix(
    in srgb,
    var(--color-secondary) 8%,
    var(--color-white)
  );

  // Блик — отдельный слой с transform: он не вызывает пересчёт раскладки,
  // в отличие от анимации background-position.
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transform: translateX(-100%);
    background-image: linear-gradient(
      90deg,
      transparent 0%,
      color-mix(in srgb, var(--color-white) 70%, transparent) 50%,
      transparent 100%
    );
    animation: base-skeleton-shimmer 1.6s ease-in-out infinite;
  }

  // Движение здесь — украшение, а не смысл: тем, кто просил его убрать,
  // остаётся статичная подложка.
  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
    }
  }
}

@keyframes base-skeleton-shimmer {
  to {
    transform: translateX(100%);
  }
}
</style>
